import {
  AccountTypeEnum,
  TransferDestinationInput,
  TransferDestinationTypeInput,
  TransferDetails,
  TransferDetailsTransferMethod,
  TransferEndpoint,
  TransferEndpointOwnership,
  TransferRail,
  TransferSourceInput,
} from '@varo/graphql-schema-types';
import type {
  TransferDestinationOption,
  TransferSourceOption,
} from 'features/feeBasedInstantTransfer/adapters/transferOptionsAdapter';
import type { TransferAmountError } from 'features/feeBasedInstantTransfer/helpers/transferAmountRules';
import type { TransferBlockingError } from 'features/feeBasedInstantTransfer/helpers/transferBlockingError';
import { TRANSFER_TYPES } from 'shared/analytics/events/transferMoney';
import {
  type ClassifiedErrorType,
  classifyError,
} from 'shared/utils/errors/classifyError';
import { CombinedError, OperationResult } from 'urql';

export interface ReviewedTransfer {
  source: TransferSourceInput;
  destination: TransferDestinationInput;
  amount: string;
  currency: string;
}

export const ANALYTICS_SOURCES = {
  amount: 'transfer_money_amount',
  confirm: 'transfer_money_confirm',
} as const;

export type AnalyticsSourceName =
  (typeof ANALYTICS_SOURCES)[keyof typeof ANALYTICS_SOURCES];

export type TransferStep = 'amount' | 'confirm' | 'submit';

export type TransferErrorType =
  ClassifiedErrorType | 'api_error' | 'validation';

/** A failure as it is reported to analytics: a stable reason code, its class and the real error string. */
export interface TransferFailure {
  reason: string;
  type: TransferErrorType;
  message?: string;
}

export type OperationName =
  | 'transfer_source_options'
  | 'transfer_destination_options'
  | 'transfer_route_details'
  | 'transfer_maximum'
  | 'transfer_review_details'
  | 'submit_transfer'
  | 'submit_push_to_card_transfer'
  | 'fetch_home_tiles';

export interface ApiResult {
  __typename?: string;
  code?: string | null;
  errorCode?: string | null;
  message?: string | null;
}

export type PressableComponentType = 'primary' | 'secondary' | 'dialog_action';

/** The skill's generic Pressable Clicked schema, named by the English CTA label. */
export const pressableAttributes = (
  pressableName: string,
  componentType: PressableComponentType,
  attributes: Record<string, string> | null = null,
) => ({
  pressable_name: pressableName,
  component_type: componentType,
  ...attributes,
});

export const UNKNOWN_ERROR_REASON = 'unknown';

export const SUBMIT_OUTCOME_UNKNOWN = 'submit_outcome_unknown';

const VARO_BANK = 'varo';

const EXTERNAL_ACH_ACCOUNT_TYPE = 'external_ach';

const EXTERNAL_DEBIT_ACCOUNT_TYPE = 'external_debit';

const AMOUNT_ERROR_REASONS: Record<TransferAmountError, string> = {
  belowMinimum: 'transfer_min_amount',
  aboveMaximum: 'transfer_max_amount',
  limitBlocked: 'transfer_limit',
  transferNotPossible: 'transfer_not_possible',
};

const BLOCKING_RETRY_ERROR_REASONS = {
  source: 'source_options_request_failed',
  destination: 'destination_options_request_failed',
  limits: 'route_details_request_failed',
} as const;

const errorReasonOf = (code?: string | null) => code || UNKNOWN_ERROR_REASON;

const formatTransferAmount = (amount: string) => Number(amount);

const isDebitCardDestination = (destination: TransferDestinationInput) =>
  destination.type === TransferDestinationTypeInput.ExternalDebitCard;

const SERVER_EXTERNAL_DEBIT_DESCRIPTOR = 'external_debit_card';

const TRANSFER_METHOD_TYPES: Record<
  TransferDetailsTransferMethod,
  string | undefined
> = {
  [TransferDetailsTransferMethod.Undefined]: undefined,
  [TransferDetailsTransferMethod.InternalTransfer]: TRANSFER_TYPES.INTERNAL,
  [TransferDetailsTransferMethod.AchPush]: TRANSFER_TYPES.ACH_PUSH,
  [TransferDetailsTransferMethod.AchPull]: TRANSFER_TYPES.ACH_PULL,
  [TransferDetailsTransferMethod.PushToCard]: TRANSFER_TYPES.PUSH_TO_CARD,
};

const endpointAccountType = ({ transferTypeDescriptor }: TransferEndpoint) =>
  transferTypeDescriptor === SERVER_EXTERNAL_DEBIT_DESCRIPTOR
    ? EXTERNAL_DEBIT_ACCOUNT_TYPE
    : transferTypeDescriptor || undefined;

const bankName = (institutionName?: string | null) =>
  institutionName?.toLowerCase() || undefined;

const endpointBank = ({ ownership, institutionName }: TransferEndpoint) =>
  ownership === TransferEndpointOwnership.Varo
    ? VARO_BANK
    : bankName(institutionName);

const transferDetailsAttributes = (details: TransferDetails | undefined) =>
  details
    ? {
        from_account_type: endpointAccountType(details.from),
        to_account_type: endpointAccountType(details.to),
        from_bank: endpointBank(details.from),
        to_bank: endpointBank(details.to),
        transfer_type: TRANSFER_METHOD_TYPES[details.transferType],
        transfer_speed: details.transferSpeedAnalyticsValue,
        transfer_fee_rate: details.feeRate,
        transfer_fee_amount: Number(details.feeAmount.amount),
      }
    : {};

const optionAccountType = (
  option?: TransferSourceOption | TransferDestinationOption,
) => {
  if (option?.kind === 'externalDebitCard') {
    return EXTERNAL_DEBIT_ACCOUNT_TYPE;
  }
  return option?.accountType === AccountTypeEnum.External
    ? EXTERNAL_ACH_ACCOUNT_TYPE
    : option?.accountProduct.toLowerCase();
};

const optionBank = (
  option?: TransferSourceOption | TransferDestinationOption,
) =>
  option?.kind === 'account' && option.accountType === AccountTypeEnum.Internal
    ? VARO_BANK
    : bankName(option?.institutionName);

const selectionTransferType = (
  source?: TransferSourceOption,
  destination?: TransferDestinationOption,
) => {
  if (!source || !destination) {
    return undefined;
  }
  if (destination.kind === 'externalDebitCard') {
    return TRANSFER_TYPES.PUSH_TO_CARD;
  }
  if (source.accountType === AccountTypeEnum.External) {
    return TRANSFER_TYPES.ACH_PULL;
  }
  return destination.accountType === AccountTypeEnum.External
    ? TRANSFER_TYPES.ACH_PUSH
    : TRANSFER_TYPES.INTERNAL;
};

/** ACH needs the source account type to tell push from pull, which the rail alone does not carry. */
export const railTransferType = (rail?: string) => {
  switch (rail) {
    case TransferRail.Internal:
      return TRANSFER_TYPES.INTERNAL;
    case TransferRail.PushToCard:
      return TRANSFER_TYPES.PUSH_TO_CARD;
    default:
      return undefined;
  }
};

export const accountOptionAttributes = (
  option: TransferSourceOption | TransferDestinationOption,
) => ({
  account_type: optionAccountType(option),
  institution_name: optionBank(option),
});

export const selectionAttributes = (
  source: TransferSourceOption | undefined,
  destination: TransferDestinationOption | undefined,
  amount: string,
  feeRate: number | undefined,
) => ({
  from_account_id: source?.id,
  to_account_id:
    destination?.kind === 'externalDebitCard' ? undefined : destination?.id,
  from_account_type: optionAccountType(source),
  to_account_type: optionAccountType(destination),
  from_bank: optionBank(source),
  to_bank: optionBank(destination),
  transfer_amount: formatTransferAmount(amount),
  transfer_type: selectionTransferType(source, destination),
  transfer_fee_rate: feeRate,
});

export const reviewedTransferAttributes = (
  reviewed: ReviewedTransfer,
  rail?: TransferRail,
  details?: TransferDetails,
) => {
  const isDebitCard = isDebitCardDestination(reviewed.destination);
  return {
    from_account_id: reviewed.source.id,
    to_account_id: isDebitCard ? undefined : reviewed.destination.id,
    transfer_amount: formatTransferAmount(reviewed.amount),
    ...transferDetailsAttributes(details),
    to_account_type:
      (details ? endpointAccountType(details.to) : undefined) ??
      (isDebitCard ? EXTERNAL_DEBIT_ACCOUNT_TYPE : undefined),
    transfer_type:
      (details ? TRANSFER_METHOD_TYPES[details.transferType] : undefined) ??
      railTransferType(rail),
  };
};

export const amountErrorFailure = (
  error: TransferAmountError,
): TransferFailure => ({
  reason: AMOUNT_ERROR_REASONS[error],
  type: 'validation',
});

export const blockingErrorFailure = (
  error: TransferBlockingError,
  hasNoSourceOptions: boolean,
): TransferFailure => {
  if (error.kind === 'retry') {
    return {
      reason: BLOCKING_RETRY_ERROR_REASONS[error.target],
      type: error.requestError
        ? classifyError(error.requestError).type
        : 'unknown',
      message: error.requestError?.message,
    };
  }
  return {
    reason: hasNoSourceOptions
      ? 'no_eligible_accounts'
      : 'transfer_not_possible',
    type: 'api_error',
    message: error.message,
  };
};

export const apiErrorFailure = (
  code: string | null | undefined,
  message?: string | null,
): TransferFailure => ({
  reason: errorReasonOf(code),
  type: 'api_error',
  message: message ?? undefined,
});

export const requestErrorFailure = (
  error: unknown,
  reason?: string,
): TransferFailure => {
  const { type } = classifyError(error);
  return { reason: reason ?? type, type, message: describeCause(error) };
};

const failureAttributes = (failure?: TransferFailure) => ({
  error_reason: failure?.reason,
  error_type: failure?.type,
  error_message: failure?.message,
});

export const flowErrorAttributes = (
  step: TransferStep,
  failure: TransferFailure,
) => ({ step_name: step, ...failureAttributes(failure) });

export const describeCause = (cause: unknown) => {
  if (cause instanceof Error) {
    return cause.message;
  }
  return typeof cause === 'string' ? cause : undefined;
};

export const correlationIdOf = (
  extensions: OperationResult['extensions'],
  error?: CombinedError,
) => {
  const correlationId =
    extensions?.correlationId ??
    error?.graphQLErrors.find(
      (graphQLError) => graphQLError.extensions?.correlationId !== undefined,
    )?.extensions?.correlationId;
  return typeof correlationId === 'string' ? correlationId : undefined;
};

const API_ERROR_TYPENAME_SUFFIX = 'Error';

/**
 * A request fails when transport or GraphQL errors, when no result returns, or
 * when the result is an API error union member (its typename ends in Error).
 */
export const operationAttributes = (
  operationName: OperationName,
  {
    result,
    error,
    extensions,
    hasResolvedWithoutResult = false,
  }: {
    result?: ApiResult | null;
    error?: unknown;
    extensions?: OperationResult['extensions'];
    /** The request resolved but returns no typed result, e.g. a cancellable query that resolves data only. */
    hasResolvedWithoutResult?: boolean;
  },
) => {
  const base = {
    operation_name: operationName,
    correlation_id: correlationIdOf(
      extensions,
      error instanceof CombinedError ? error : undefined,
    ),
  };
  if (error) {
    return {
      ...base,
      outcome: 'failure',
      ...failureAttributes(requestErrorFailure(error)),
    };
  }
  if (hasResolvedWithoutResult) {
    return { ...base, outcome: 'success' };
  }
  const typename = result?.__typename;
  if (!typename) {
    return { ...base, outcome: 'failure', result_type: 'missing' };
  }
  const isApiError = typename.endsWith(API_ERROR_TYPENAME_SUFFIX);
  return {
    ...base,
    outcome: isApiError ? 'failure' : 'success',
    result_type: typename,
    ...failureAttributes(
      isApiError
        ? apiErrorFailure(result.errorCode ?? result.code, result.message)
        : undefined,
    ),
  };
};
